/**
 * SystemConfig 管理页面
 * 管理系统配置项的CRUD操作
 */
import React, { useCallback, useEffect, useState } from 'react'
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  message,
  Space,
  Popconfirm,
  Tag,
  Typography,
  InputNumber,
  Alert,
  Spin,
  Tooltip,
} from 'antd'
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined,
  SettingOutlined,
  CodeOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  CopyOutlined,
  FullscreenOutlined,
  FullscreenExitOutlined,
} from '@ant-design/icons'
import { systemConfigApi } from '@/services/api'
import type { SystemConfig } from '@/types/system'

const { Title, Text } = Typography
const { TextArea } = Input

// 格式化 JSON 用于显示
const formatJsonForDisplay = (value: any): string => {
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value)
      return JSON.stringify(parsed, null, 2)
    } catch {
      return value
    }
  }
  return JSON.stringify(value, null, 2)
}

// 简化 JSON 显示（用于列表）
const simplifyJsonDisplay = (value: any, maxLen: number = 80): string => {
  const str = typeof value === 'string' ? value : JSON.stringify(value)
  if (str.length <= maxLen) return str
  return str.substring(0, maxLen) + '...'
}

// 检测 JSON 类型
const getJsonType = (value: any): { type: string; color: string } => {
  if (value === null) return { type: 'null', color: 'default' }
  if (typeof value === 'object') {
    if (Array.isArray(value)) return { type: 'array', color: 'purple' }
    return { type: 'object', color: 'blue' }
  }
  if (typeof value === 'number') return { type: 'number', color: 'green' }
  if (typeof value === 'boolean') return { type: 'boolean', color: 'orange' }
  if (typeof value === 'string') return { type: 'string', color: 'cyan' }
  return { type: 'unknown', color: 'default' }
}

const SystemConfigManagement: React.FC = () => {
  const [loading, setLoading] = useState(false)
  const [data, setData] = useState<SystemConfig[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [searchKey, setSearchKey] = useState('')

  const [modalVisible, setModalVisible] = useState(false)
  const [modalTitle, setModalTitle] = useState('')
  const [editingKey, setEditingKey] = useState<string | null>(null)
  const [editingRecord, setEditingRecord] = useState<SystemConfig | null>(null)
  const [form] = Form.useForm()
  const [submitting, setSubmitting] = useState(false)

  const [jsonError, setJsonError] = useState<string | null>(null)

  // 当弹窗打开时，确保表单值已设置（使用 setTimeout 延迟确保 Modal 渲染完成）
  useEffect(() => {
    console.log('[DEBUG] useEffect triggered, modalVisible:', modalVisible, 'editingRecord:', editingRecord)
    if (modalVisible && editingRecord) {
      // 延迟设置表单值，确保 Modal 完全渲染
      const timer = setTimeout(() => {
        const displayValue = formatConfigValue(editingRecord)
        console.log('[DEBUG] useEffect setting form value (delayed):', displayValue)
        form.setFieldsValue({
          config_key: editingRecord.config_key,
          config_value: displayValue,
          description: editingRecord.description || '',
        })
        setJsonError(null)
      }, 100)
      return () => clearTimeout(timer)
    }
  }, [modalVisible, editingRecord, form])

  // 获取配置列表
  const fetchData = useCallback(async () => {
    setLoading(true)
    try {
      const response = await systemConfigApi.getConfigs({
        page,
        page_size: pageSize,
        search: searchKey || undefined,
      })
      setData(response.data.items || [])
      setTotal(response.data.total || 0)
    } catch (error: any) {
      message.error(`获取配置列表失败: ${error.message || '未知错误'}`)
    } finally {
      setLoading(false)
    }
  }, [page, pageSize, searchKey])

  // 初始化
  useEffect(() => {
    fetchData()
  }, [fetchData])

  // 打开创建模态框
  const handleCreate = () => {
    setModalTitle('创建配置')
    setEditingKey(null)
    setEditingRecord(null)
    form.resetFields()
    setJsonError(null)
    setModalVisible(true)
  }

  // 辅助函数：格式化 config_value 为显示字符串
  const formatConfigValue = (record: SystemConfig): string => {
    console.log('[DEBUG] formatConfigValue called with record:', record)
    console.log('[DEBUG] config_value:', record?.config_value, 'type:', typeof record?.config_value)

    const value = record?.config_value
    if (value === null || value === undefined) {
      console.log('[DEBUG] value is null/undefined, returning empty string')
      return ''
    }
    if (typeof value === 'string') {
      console.log('[DEBUG] value is string:', value)
      const trimmed = value.trim()
      // 尝试解析 JSON 字符串
      if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
        try {
          const parsed = JSON.parse(trimmed)
          console.log('[DEBUG] parsed JSON:', parsed)
          return JSON.stringify(parsed, null, 2)
        } catch {
          // 解析失败，返回原始字符串
          console.log('[DEBUG] JSON parse failed, returning original string')
          return value
        }
      }
      // 非 JSON 字符串直接返回
      console.log('[DEBUG] not JSON, returning original string')
      return value
    }
    if (typeof value === 'object') {
      console.log('[DEBUG] value is object')
      return JSON.stringify(value, null, 2)
    }
    console.log('[DEBUG] converting to string:', String(value))
    return String(value)
  }

  // 打开编辑模态框
  const handleEdit = (record: SystemConfig) => {
    console.log('[DEBUG] handleEdit called with record:', record)
    setModalTitle('编辑配置')
    setEditingKey(record.config_key)
    setEditingRecord(record)

    // 同步设置表单值（确保在弹窗打开前值已设置）
    const displayValue = formatConfigValue(record)
    console.log('[DEBUG] handleEdit setting form, displayValue:', displayValue)
    form.setFieldsValue({
      config_key: record.config_key,
      config_value: displayValue,
      description: record.description || '',
    })
    setJsonError(null)
    setModalVisible(true)
  }

  // 关闭弹窗时清理状态
  const handleCloseModal = () => {
    setModalVisible(false)
    setEditingKey(null)
    setEditingRecord(null)
    form.resetFields()
    setJsonError(null)
  }

  // 格式化 JSON
  const handleFormatJson = () => {
    const value = form.getFieldValue('config_value')
    if (!value) return

    try {
      const parsed = JSON.parse(value)
      form.setFieldsValue({
        config_value: JSON.stringify(parsed, null, 2),
      })
      setJsonError(null)
      message.success('JSON 格式化成功')
    } catch (e) {
      setJsonError('JSON 格式错误，无法格式化')
      message.error('JSON 格式错误，无法格式化')
    }
  }

  // 复制 JSON
  const handleCopyJson = () => {
    const value = form.getFieldValue('config_value')
    if (!value) return

    navigator.clipboard.writeText(value).then(() => {
      message.success('已复制到剪贴板')
    }).catch(() => {
      message.error('复制失败')
    })
  }

  // 验证JSON
  const validateJSON = (value: string): boolean => {
    if (!value.trim()) {
      setJsonError('配置值不能为空')
      return false
    }

    // 只对 json 和 integer 类型验证 JSON 格式
    const configType = editingRecord?.config_type
    if (configType === 'json' || configType === 'integer') {
      try {
        JSON.parse(value)
        setJsonError(null)
        return true
      } catch (e) {
        setJsonError('JSON格式错误: ' + (e as Error).message)
        return false
      }
    }

    setJsonError(null)
    return true
  }

  // 提交表单
  const handleSubmit = async () => {
    try {
      const values = await form.validateFields()

      // 验证JSON
      if (!validateJSON(values.config_value)) {
        return
      }

      setSubmitting(true)

      // 直接使用字符串，不需要解析成对象
      // 后端期望接收 JSON 字符串
      const configType = editingRecord?.config_type || 'json'

      const submitData = {
        config_value: values.config_value, // 直接发送字符串
        config_type: configType,
        description: values.description,
      }

      if (editingKey) {
        // 更新
        await systemConfigApi.updateConfig(editingKey, submitData)
        message.success('更新配置成功')
      } else {
        // 创建
        await systemConfigApi.createConfig({
          config_key: values.config_key,
          ...submitData,
        })
        message.success('创建配置成功')
      }

      handleCloseModal()
      fetchData()
    } catch (error: any) {
      if (error.errorFields) {
        return
      }
      message.error(`操作失败: ${error.message || '未知错误'}`)
    } finally {
      setSubmitting(false)
    }
  }

  // 删除配置
  const handleDelete = async (key: string) => {
    try {
      await systemConfigApi.deleteConfig(key)
      message.success('删除配置成功')
      fetchData()
    } catch (error: any) {
      message.error(`删除失败: ${error.message || '未知错误'}`)
    }
  }

  // 初始化默认配置
  const handleInitDefaults = async () => {
    Modal.confirm({
      title: '初始化默认配置',
      content: '确定要初始化所有默认配置吗？这将创建系统所需的默认配置项。',
      onOk: async () => {
        try {
          await systemConfigApi.initDefaults()
          message.success('初始化默认配置成功')
          fetchData()
        } catch (error: any) {
          message.error(`初始化失败: ${error.message || '未知错误'}`)
        }
      },
    })
  }

  // 表格列定义
  const columns = [
    {
      title: '配置键',
      dataIndex: 'config_key',
      key: 'config_key',
      width: 250,
      render: (text: string) => (
        <Space>
          <CodeOutlined className="text-blue-500" />
          <Text strong className="font-mono">{text}</Text>
        </Space>
      ),
    },
    {
      title: '类型',
      dataIndex: 'config_type',
      key: 'config_type',
      width: 100,
      render: (type: string | null) => {
        const colorMap: Record<string, string> = {
          json: 'purple',
          string: 'cyan',
          number: 'green',
          boolean: 'orange',
        }
        return <Tag color={colorMap[type || ''] || 'default'}>{type || '-'}</Tag>
      },
    },
    {
      title: '描述',
      dataIndex: 'description',
      key: 'description',
      width: 200,
      render: (text: string | null) => text || '-',
    },
    {
      title: '更新时间',
      dataIndex: 'updated_at',
      key: 'updated_at',
      width: 180,
      render: (text: string | null) =>
        text ? new Date(text).toLocaleString() : '-',
    },
    {
      title: '操作',
      key: 'action',
      width: 150,
      fixed: 'right' as const,
      render: (_: any, record: SystemConfig) => (
        <Space size="small">
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >编辑</Button>
          <Popconfirm
            title="确定删除此配置？"
            description="删除后无法恢复，请谨慎操作"
            onConfirm={() => handleDelete(record.config_key)}
            okText="删除"
            cancelText="取消"
            okButtonProps={{ danger: true }}
          >
            <Button type="text" danger icon={<DeleteOutlined />} >删除</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ]

  return (
    <div className="p-6">
      {/* 页面标题和操作栏 */}
      <Card className="mb-4">
        <Space className="w-full justify-between" wrap>
          <div>
            <h2 className="text-xl font-bold flex items-center gap-2">
              <SettingOutlined />
              系统配置管理
            </h2>
            <p className="text-gray-500 text-sm">管理系统配置项，支持JSON格式的配置值</p>
          </div>
          <Space>
            <Button
              icon={<ReloadOutlined />}
              onClick={fetchData}
              loading={loading}
            >
              刷新
            </Button>
            <Button onClick={handleInitDefaults}>
              初始化默认配置
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleCreate}
            >
              创建配置
            </Button>
          </Space>
        </Space>

        {/* 搜索栏 */}
        <div className="mt-4">
          <Input.Search
            placeholder="搜索配置键或描述"
            value={searchKey}
            onChange={(e) => setSearchKey(e.target.value)}
            onSearch={() => {
              setPage(1)
              fetchData()
            }}
            style={{ width: 300 }}
            allowClear
          />
        </div>
      </Card>

      {/* 数据表格 */}
      <Card>
        <Table
          columns={columns}
          dataSource={data}
          rowKey="config_key"
          loading={loading}
          pagination={{
            current: page,
            pageSize: pageSize,
            total: total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `共 ${total} 条记录`,
            onChange: (p, ps) => {
              setPage(p)
              setPageSize(ps || 10)
            },
          }}
          scroll={{ x: 1000 }}
        />
      </Card>

      {/* 创建/编辑模态框 */}
      <Modal
        title={modalTitle}
        open={modalVisible}
        onOk={handleSubmit}
        onCancel={handleCloseModal}
        confirmLoading={submitting}
        width={700}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          preserve={false}
        >
          <Form.Item
            name="config_key"
            label="配置键"
            rules={[
              { required: true, message: '请输入配置键' },
              { pattern: /^[a-z0-9_]+$/, message: '只能使用小写字母、数字和下划线' },
            ]}
          >
            <Input
              placeholder="例如: vip_thresholds"
              disabled={!!editingKey}
              prefix={<CodeOutlined />}
            />
          </Form.Item>

          <Form.Item
            name="config_value"
            label={`配置值 (${editingRecord?.config_type === 'json' ? 'JSON格式' : editingRecord?.config_type === 'integer' ? '数字' : '字符串格式'})`}
            rules={[
              { required: true, message: '请输入配置值' },
              {
                validator: (_, value) => {
                  if (!value) return Promise.resolve()

                  // 只对 JSON 类型验证 JSON 格式
                  if (editingRecord?.config_type === 'json' || editingRecord?.config_type === 'integer') {
                    try {
                      JSON.parse(value)
                      return Promise.resolve()
                    } catch (e) {
                      return Promise.reject('JSON格式错误')
                    }
                  }
                  return Promise.resolve()
                },
              },
            ]}
          extra={
              <Space className="mt-1">
                <Button
                  type="text"
                  size="small"
                  icon={<CodeOutlined />}
                  onClick={handleFormatJson}
                  title="格式化 JSON"
                >
                  格式化
                </Button>
                <Button
                  type="text"
                  size="small"
                  icon={<CopyOutlined />}
                  onClick={handleCopyJson}
                  title="复制 JSON"
                >
                  复制
                </Button>
              </Space>
            }
          >
            <TextArea
              rows={12}
              placeholder={`{\n  "key": "value"\n}`}
              onChange={(e) => validateJSON(e.target.value)}
              style={{ fontFamily: 'monospace', fontSize: '12px' }}
            />
          </Form.Item>

          {jsonError ? (
            <Alert
              message={jsonError}
              type="error"
              showIcon
              className="mb-4"
            />
          ) : (
            <Alert
              message="支持 JSON 格式，点击图标按钮可格式化或复制"
              type="info"
              showIcon
              className="mb-4"
            />
          )}

          <Form.Item
            name="description"
            label="描述"
          >
            <TextArea
              rows={2}
              placeholder="配置项的描述说明"
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}

export default SystemConfigManagement
